import React, { useState, useEffect } from 'react';
import { X, User, CreditCard, Target, Settings, Bell, MessageSquare, Heart, Gavel, History, Package, Lock, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import MyProfileTab from './MyProfileTab';
import PaymentMethodsTab from './PaymentMethodsTab';
import PaddleSettingsTab from './PaddleSettingsTab';
import NotificationsTab from './NotificationsTab';
import MessagesTab from './MessagesTab';
import SavedLotsTab from './SavedLotsTab';
import MyBidsTab from './MyBidsTab';
import SavedArtworksTab from './SavedArtworksTab';
import MultipleAddressManager from './MultipleAddressManager';
import { hasDashboardFullAccess } from '@/lib/flags';

interface UserDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: string;
}

const UserDashboard: React.FC<UserDashboardProps> = ({ 
  isOpen, 
  onClose, 
  initialTab = 'profile' 
}) => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isTabListOpen, setIsTabListOpen] = useState(false);
  
  const isGuest = !user;
  const fullAccess = hasDashboardFullAccess(user?.email);

  // Sync activeTab with initialTab when it changes
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
      setIsTabListOpen(false);
    }
  }, [initialTab, isOpen]);

  // Lock body scroll when dashboard is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;
  // Dashboard is globally enabled; access is controlled per-tab via fullAccess

  const tabs = [
    {
      id: 'profile',
      label: 'My Profile',
      icon: User,
      description: 'Manage your personal information and preferences',
      guestAccess: true
    },
    {
      id: 'bids',
      label: 'My Bids',
      icon: Gavel,
      description: 'View and manage your current and past bids',
      guestAccess: false
    },
    {
      id: 'saved-lots',
      label: 'Saved Lots',
      icon: Heart,
      description: 'Lots you have saved to watch or bid on',
      guestAccess: false
    },
    {
      id: 'saved-artworks',
      label: 'Saved Artworks',
      icon: Package,
      description: 'Artworks you have saved to your watchlist',
      guestAccess: false
    },
    {
      id: 'payment',
      label: 'Payment Methods',
      icon: CreditCard,
      description: 'Secure payment options and transaction history',
      guestAccess: false
    },
    {
      id: 'addresses',
      label: 'Shipping Addresses',
      icon: MapPin,
      description: 'Manage your shipping and billing addresses for deliveries',
      guestAccess: false
    },
    {
      id: 'paddle',
      label: 'Paddle Settings',
      icon: Target,
      description: 'Bidding preferences and safety features',
      guestAccess: false
    }
  ];

  const handleTabClick = (tabId: string, guestAccess: boolean) => {
    if (isGuest && !guestAccess) {
      onClose();
      navigate('/login');
      return;
    }
    setActiveTab(tabId);
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      setIsTabListOpen(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[40] flex items-start justify-center p-0 sm:p-4 backdrop-glass overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="backdrop-glass-weak border border-white/20 sm:rounded-3xl rounded-none shadow-2xl w-full max-w-6xl sm:h-[90vh] sm:max-h-[900px] h-[100svh] flex flex-col overflow-hidden relative z-[40] sm:mt-4 mt-0"
      >
        {/* Header with Glass Morphism */}
        <div className="relative px-4 sm:px-8 py-4 sm:py-6 border-b border-white/10 bg-gradient-to-r from-slate-900/60 via-slate-800/50 to-slate-900/60">
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="p-3 sm:p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-sm shadow-lg">
                <User className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
                  Account Dashboard
                </h1>
                <p className="text-white/70 font-light mt-1">
                  {user ? `Welcome back, ${user.email}` : 'Manage your account settings'}
                </p>
              </div>
            </div>

            <Button 
              variant="ghost" 
              onClick={onClose}
              className="p-2 sm:p-3 rounded-2xl hover:bg-white/10 transition-all duration-300 group border border-transparent hover:border-white/20"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6 text-white/80 group-hover:text-white group-hover:rotate-90 transition-all duration-300" />
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            {/* Tab Navigation */}
            <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-white/10 bg-transparent">
              <div className="relative flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsTabListOpen((prev) => !prev)}
                  className="sm:hidden bg-transparent text-white/80 hover:text-white hover:bg-white/10"
                >
                  {isTabListOpen ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-2" />
                      Hide Tabs
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-2" />
                      Click to Show Tabs
                    </>
                  )}
                </Button>
                <TabsList className={cn(
                  "flex flex-wrap md:flex-nowrap items-center justify-center gap-2 md:gap-3 px-3 md:px-4 py-3 md:py-4 rounded-3xl bg-white/5/60 backdrop-blur-xl border border-white/10 shadow-[0_8px_30px_rgba(15,23,42,0.25)] !h-auto !bg-transparent",
                  isTabListOpen ? 'flex' : 'hidden',
                  'sm:flex'
                )}>
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isLocked = isGuest && !tab.guestAccess;
                  const isDisabled = (tab as any).disabled;
                  
                  return (
                    <TabsTrigger 
                      key={tab.id}
                      value={tab.id}
                      onClick={() => {
                        if (isDisabled) return;
                        handleTabClick(tab.id, tab.guestAccess)
                      }}
                      className={cn(
                        "group flex flex-col items-center justify-start gap-2 rounded-2xl px-3 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm transition-all duration-300 font-medium whitespace-nowrap md:flex-1 md:basis-0 md:min-w-[140px]",
                        isLocked 
                          ? "text-white/40 cursor-pointer hover:bg-red-500/10 hover:text-red-300" 
                          : isDisabled
                            ? "text-white/40 cursor-not-allowed"
                            : "text-white/70 hover:bg-white/10 hover:text-white",
                        "data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500/50 data-[state=active]:to-teal-500/50 data-[state=active]:text-slate-900 data-[state=active]:shadow-lg data-[state=active]:border data-[state=active]:border-white/40"
                      )}
                    >
                      {isLocked || isDisabled ? (
                        <>
                          <Lock className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                          <span className="text-xs sm:text-sm font-medium">{tab.label}</span>
                        </>
                      ) : (
                        <>
                          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-white shadow-inner transition group-data-[state=active]:bg-white group-data-[state=active]:text-emerald-600">
                            <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </span>
                          <span className="mt-1 text-white/80 group-data-[state=active]:text-slate-900 sm:text-sm text-xs group-data-[state=active]:translate-y-1 transition-transform">
                            {tab.label}
                          </span>
                        </>
                      )}
                    </TabsTrigger>
                  );
                })}
                </TabsList>
              </div>

              {/* Tab Description */}
              <div className="mt-3 text-center px-4">
                <p className="text-white/70 text-sm sm:text-base font-light">
                  {tabs.find(tab => tab.id === activeTab)?.description}
                  {isGuest && activeTab !== 'profile' && (
                    <span className="block mt-2 text-red-300 font-medium">
                      🔒 Please sign in to access this feature
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden">
              <TabsContent value="profile" className="h-full m-0">
                <MyProfileTab />
              </TabsContent>
              
              {!isGuest && (
                <>
                  <TabsContent value="bids" className="h-full m-0">
                    <MyBidsTab />
                  </TabsContent>
                  
                  <TabsContent value="saved-lots" className="h-full m-0">
                    <SavedLotsTab />
                  </TabsContent>
                  
                  {fullAccess ? (
                    <TabsContent value="saved-artworks" className="h-full m-0">
                      <SavedArtworksTab />
                    </TabsContent>
                  ) : (
                    <TabsContent value="saved-artworks" className="h-full m-0">
                      <div className="h-full w-full flex items-center justify-center text-white/70">
                        This section is temporarily unavailable while we work on improvements.
                      </div>
                    </TabsContent>
                  )}
                  
                  {fullAccess ? (
                    <TabsContent value="messages" className="h-full m-0">
                      <MessagesTab />
                    </TabsContent>
                  ) : (
                    <TabsContent value="messages" className="h-full m-0">
                      <div className="h-full w-full flex items-center justify-center text-white/70">
                        This section is temporarily unavailable while we work on improvements.
                      </div>
                    </TabsContent>
                  )}
                  
                  {fullAccess ? (
                    <TabsContent value="notifications" className="h-full m-0">
                      <NotificationsTab />
                    </TabsContent>
                  ) : (
                    <TabsContent value="notifications" className="h-full m-0">
                      <div className="h-full w-full flex items-center justify-center text-white/70">
                        This section is temporarily unavailable while we work on improvements.
                      </div>
                    </TabsContent>
                  )}
                  
                  <TabsContent value="payment" className="h-full m-0">
                    <PaymentMethodsTab />
                  </TabsContent>
                  
                  <TabsContent value="addresses" className="h-full m-0">
                    <div className="h-full overflow-auto p-8">
                      <MultipleAddressManager />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="paddle" className="h-full m-0">
                    <PaddleSettingsTab />
                  </TabsContent>
                </>
              )}
            </div>
          </Tabs>
        </div>
      </motion.div>
    </div>
  );
};

export default UserDashboard;